import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { WidgetTable } from '@/components/WidgetTable';
import { RiskBadge } from '@/pages/organization/tabs/OrgShadowAITab/shared';
import { useOrgTopShadowAiTools } from '@/hooks/api/useShadowAiTools';
import type { ShadowAiTopTool } from '@kirin/api-client/contracts/shadow-ai';
import { ClickableCell } from '@/pages/organization/components/ClickableCell';
import { AIToolUsageUserList } from '@/pages/organization/components/WidgetUserLists/AIToolUsageUserList';

type AIToolSummary = {
  toolId: string;
  name: string;
  riskLevel: ShadowAiTopTool['riskLevel'];
  userCount: number;
  category: string;
};

export function MostActiveAITool() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data, isLoading, isError, refetch } = useOrgTopShadowAiTools(
    organizationId,
    {
      limit: 10,
    }
  );

  const tools: AIToolSummary[] =
    data?.tools.map(
      (tool: ShadowAiTopTool): AIToolSummary => ({
        toolId: tool.toolId,
        name: tool.name,
        riskLevel: tool.riskLevel,
        userCount: tool.userCount,
        category: tool.category,
      })
    ) ?? [];

  const [selectedTool, setSelectedTool] = useState<AIToolSummary | null>(null);

  const handleUsersClick = (tool: AIToolSummary) => {
    setSelectedTool(tool);
  };

  const handleCloseModal = () => {
    setSelectedTool(null);
  };

  return (
    <>
      <DashboardWidgetWrapper
        title="Most Active AI Tools"
        description="Shows up to 10 AI Tools"
        className="xl:col-span-6"
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
      >
        <div className="flex flex-col gap-4 h-full">
          <WidgetTable<AIToolSummary>
            data={tools}
            emptyState="No tools found"
            className="max-h-none"
            // Rows are (tool, category); one tool can fill two rows.
            getRowId={tool => `${tool.toolId}::${tool.category}`}
            columns={[
              {
                id: 'name',
                header: 'Name',
                cell: tool => (
                  <span
                    className="font-medium truncate block max-w-[200px]"
                    title={tool.name}
                  >
                    {tool.name}
                  </span>
                ),
                className: 'min-w-[160px]',
              },
              {
                id: 'severity',
                header: 'Severity',
                cell: tool => <RiskBadge level={tool.riskLevel} />,
                className: 'min-w-[100px]',
              },
              {
                id: 'users',
                header: 'Users',
                cell: tool => (
                  <ClickableCell
                    label={`${tool.userCount}`}
                    onClick={() => handleUsersClick(tool)}
                  />
                ),
                className: 'min-w-[80px]',
              },
            ]}
          />
        </div>
      </DashboardWidgetWrapper>
      <AIToolUsageUserList
        open={selectedTool !== null}
        onClose={handleCloseModal}
        toolName={selectedTool?.name || ''}
        toolId={selectedTool?.toolId || ''}
        category={selectedTool?.category}
      />
    </>
  );
}
